import React from 'react';
import { X, Keyboard } from 'lucide-react';

interface ShortcutGroup {
  label: string;
  shortcuts: Array<{ keys: string; description: string }>;
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    label: 'General',
    shortcuts: [
      { keys: 'Shift+P', description: 'Toggle Preview' },
      { keys: 'E', description: 'Open Export Dialog' },
      { keys: 'Ctrl+Shift+S', description: 'Save as Template' },
      { keys: 'Escape', description: 'Deselect / Exit Preview' },
      { keys: '?', description: 'Toggle Shortcuts Panel' },
    ],
  },
  {
    label: 'Tools',
    shortcuts: [
      { keys: 'V', description: 'Select Tool' },
      { keys: 'T', description: 'Text Tool' },
      { keys: 'R', description: 'Rectangle Tool' },
      { keys: 'O', description: 'Ellipse Tool' },
      { keys: 'L', description: 'Line Tool' },
      { keys: 'P', description: 'Pen Tool' },
      { keys: 'B', description: 'Button Tool' },
      { keys: 'I', description: 'Image Tool' },
      { keys: 'Ctrl+Shift+I', description: 'Import SVG' },
      { keys: 'H', description: 'Pan Tool' },
    ],
  },
  {
    label: 'Edit',
    shortcuts: [
      { keys: 'Ctrl+Z', description: 'Undo' },
      { keys: 'Ctrl+Shift+Z', description: 'Redo' },
      { keys: 'Ctrl+C', description: 'Copy' },
      { keys: 'Ctrl+V', description: 'Paste' },
      { keys: 'Ctrl+X', description: 'Cut' },
      { keys: 'Ctrl+D', description: 'Duplicate' },
      { keys: 'Delete', description: 'Delete Selected' },
      { keys: 'Ctrl+A', description: 'Select All' },
      { keys: 'Ctrl+Shift+U', description: 'Attach/Detach Text to Path' },
    ],
  },
  {
    label: 'Arrange',
    shortcuts: [
      { keys: 'Ctrl+G', description: 'Group' },
      { keys: 'Ctrl+Shift+G', description: 'Ungroup' },
      { keys: ']', description: 'Bring Forward' },
      { keys: '[', description: 'Send Backward' },
      { keys: 'Ctrl+]', description: 'Bring to Front' },
      { keys: 'Ctrl+[', description: 'Send to Back' },
    ],
  },
  {
    label: 'Align & Distribute',
    shortcuts: [
      { keys: 'Ctrl+Shift+L', description: 'Align Left' },
      { keys: 'Ctrl+Shift+C', description: 'Align Center (Horizontal)' },
      { keys: 'Ctrl+Shift+R', description: 'Align Right' },
      { keys: 'Ctrl+Shift+T', description: 'Align Top' },
      { keys: 'Ctrl+Shift+M', description: 'Align Middle (Vertical)' },
      { keys: 'Ctrl+Shift+B', description: 'Align Bottom' },
      { keys: 'Align To', description: "Respects the toolbar's Align To setting — Selection needs 2+, Artboard works with 1+" },
    ],
  },
  {
    label: 'Viewport',
    shortcuts: [
      { keys: 'Ctrl+= / Ctrl+-', description: 'Zoom In / Out' },
      { keys: 'Ctrl+0', description: 'Reset to 100%' },
      { keys: 'Ctrl+1', description: 'Fit to Screen' },
      { keys: 'G', description: 'Toggle Grid' },
      { keys: 'Ctrl+R', description: 'Toggle Rulers' },
      { keys: 'Ctrl+;', description: 'Toggle Guides' },
      { keys: 'Ctrl+Shift+;', description: 'Clear All Guides' },
      { keys: 'Space+Drag', description: 'Pan Canvas' },
      { keys: 'Scroll', description: 'Zoom at Cursor' },
    ],
  },
  {
    label: 'Pen Tool',
    shortcuts: [
      { keys: 'Click', description: 'Add Anchor Point' },
      { keys: 'Click+Drag', description: 'Add Anchor with Curves' },
      { keys: 'Enter', description: 'Finish Open Path' },
      { keys: 'Escape', description: 'Cancel Drawing' },
      { keys: 'Backspace', description: 'Remove Last Anchor' },
    ],
  },
  {
    label: 'Preview Mode',
    shortcuts: [
      { keys: '1 / 2 / 3', description: 'Switch Preview Modes' },
      { keys: 'B', description: 'Toggle Boundaries' },
      { keys: 'L', description: 'Toggle Layers' },
      { keys: 'A', description: 'Toggle ADA Panel' },
      { keys: '+ / -', description: 'Zoom In / Out' },
      { keys: '0', description: 'Reset Zoom' },
    ],
  },
];

function KeyBadge({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5
      bg-white/[0.06] border border-white/[0.1] rounded text-[11px] text-white/70
      font-mono leading-none">
      {children}
    </kbd>
  );
}

function formatKeys(keys: string) {
  return keys.split('+').map((key, i) => (
    <React.Fragment key={i}>
      {i > 0 && <span className="text-white/20 mx-0.5">+</span>}
      <KeyBadge>{key.trim()}</KeyBadge>
    </React.Fragment>
  ));
}

interface KeyboardShortcutsPanelProps {
  onClose: () => void;
}

export default function KeyboardShortcutsPanel({ onClose }: KeyboardShortcutsPanelProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#12121a] border border-white/[0.06] rounded-xl shadow-2xl w-[520px] max-h-[80vh] overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Keyboard size={18} className="text-cyan-400" />
            <h2 className="text-sm font-semibold text-white">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-white/40 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-60px)] p-5 space-y-5 sidebar-scroll">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              <h3 className="text-[10px] uppercase tracking-wider text-white/30 font-medium mb-2">
                {group.label}
              </h3>
              <div className="space-y-1.5">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.keys}
                    className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-white/[0.03]"
                  >
                    <span className="text-xs text-white/60">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.includes(' / ')
                        ? shortcut.keys.split(' / ').map((k, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <span className="text-white/20 text-xs mx-1">/</span>}
                              {formatKeys(k)}
                            </React.Fragment>
                          ))
                        : formatKeys(shortcut.keys)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-white/[0.06] text-center">
          <span className="text-[10px] text-white/20">
            Press <KeyBadge>?</KeyBadge> to toggle this panel
          </span>
        </div>
      </div>
    </div>
  );
}
